import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Download, Settings, AlertCircle, CheckCircle, FileText, Eye, X } from 'lucide-react'
import { supabase } from '../../../lib/supabase'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'

interface PreviewData {
  title: string
  type: 'inspections' | 'problems'
  rows: string[][]
  headers: string[]
  machineMap: Record<string, any>
  rawData: any[]
}

export const ReportsModule: React.FC = () => {
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)
  const [status, setStatus] = useState<{ type: 'success' | 'error' | 'info'; message: string } | null>(null)
  const [preview, setPreview] = useState<PreviewData | null>(null)

  const showStatus = (type: 'success' | 'error' | 'info', message: string) => {
    setStatus({ type, message })
    setTimeout(() => setStatus(null), 5000)
  }

  const loadMachineMap = async () => {
    const { data } = await supabase.from('machines').select('id, name, hal').eq('status', 'active')
    return (data || []).reduce((acc: any, m: any) => { acc[m.id] = m; return acc }, {})
  }

  const handlePreview = async (type: string) => {
    setIsLoading(true)
    setPreview(null)
    showStatus('info', 'Data laden...')
    try {
      const machineMap = await loadMachineMap()

      if (type === 'today') {
        const today = new Date().toISOString().split('T')[0]
        const { data } = await supabase.from('quality_control').select('*').gte('created_at', today + 'T00:00:00.000Z').lte('created_at', today + 'T23:59:59.999Z').order('created_at', { ascending: false })
        setPreview({ title: 'Inspecties – Vandaag', type: 'inspections', rawData: data || [], machineMap, headers: ['Datum/Tijd', 'Machine', 'Hal', 'Medewerker', 'Product', 'OK', 'Opmerking'], rows: (data || []).map((r: any) => [new Date(r.created_at).toLocaleString('nl-NL'), machineMap[r.machine_id]?.name || '–', machineMap[r.machine_id]?.hal || '–', r.employee_name || '–', r.product_number || '–', r.meets_requirements ? 'Ja' : 'Nee', r.comments || '']) })
      } else if (type === 'week') {
        const weekAgo = new Date(Date.now() - 7 * 86400000).toISOString().split('T')[0]
        const today = new Date().toISOString().split('T')[0]
        const { data } = await supabase.from('quality_control').select('*').gte('created_at', weekAgo + 'T00:00:00.000Z').lte('created_at', today + 'T23:59:59.999Z').order('created_at', { ascending: false })
        setPreview({ title: 'Inspecties – Deze Week', type: 'inspections', rawData: data || [], machineMap, headers: ['Datum/Tijd', 'Machine', 'Hal', 'Medewerker', 'Product', 'OK', 'Opmerking'], rows: (data || []).map((r: any) => [new Date(r.created_at).toLocaleString('nl-NL'), machineMap[r.machine_id]?.name || '–', machineMap[r.machine_id]?.hal || '–', r.employee_name || '–', r.product_number || '–', r.meets_requirements ? 'Ja' : 'Nee', r.comments || '']) })
      } else if (type === 'month') {
        const monthAgo = new Date(Date.now() - 30 * 86400000).toISOString().split('T')[0]
        const today = new Date().toISOString().split('T')[0]
        const { data } = await supabase.from('quality_control').select('*').gte('created_at', monthAgo + 'T00:00:00.000Z').lte('created_at', today + 'T23:59:59.999Z').order('created_at', { ascending: false })
        setPreview({ title: 'Inspecties – Deze Maand', type: 'inspections', rawData: data || [], machineMap, headers: ['Datum/Tijd', 'Machine', 'Hal', 'Medewerker', 'Product', 'OK', 'Opmerking'], rows: (data || []).map((r: any) => [new Date(r.created_at).toLocaleString('nl-NL'), machineMap[r.machine_id]?.name || '–', machineMap[r.machine_id]?.hal || '–', r.employee_name || '–', r.product_number || '–', r.meets_requirements ? 'Ja' : 'Nee', r.comments || '']) })
      } else if (type === 'problems-open') {
        const { data } = await supabase.from('probleem_meldingen').select('*').eq('oplossing_gevonden', false).order('datum_tijd', { ascending: false })
        setPreview({ title: 'Probleemmeldingen – Open', type: 'problems', rawData: data || [], machineMap, headers: ['Datum/Tijd', 'Machine', 'Hal', 'Melder', 'Product', 'Status', 'Omschrijving'], rows: (data || []).map((r: any) => [new Date(r.datum_tijd).toLocaleString('nl-NL'), machineMap[r.machine_id]?.name || '–', machineMap[r.machine_id]?.hal || '–', r.gebruiker_naam || '–', r.productcode || '–', r.oplossing_gevonden ? 'Opgelost' : 'Open', r.argumentatie || '']) })
      } else if (type === 'problems-all') {
        const { data } = await supabase.from('probleem_meldingen').select('*').order('datum_tijd', { ascending: false })
        setPreview({ title: 'Probleemmeldingen – Alle', type: 'problems', rawData: data || [], machineMap, headers: ['Datum/Tijd', 'Machine', 'Hal', 'Melder', 'Product', 'Status', 'Omschrijving'], rows: (data || []).map((r: any) => [new Date(r.datum_tijd).toLocaleString('nl-NL'), machineMap[r.machine_id]?.name || '–', machineMap[r.machine_id]?.hal || '–', r.gebruiker_naam || '–', r.productcode || '–', r.oplossing_gevonden ? 'Opgelost' : 'Open', r.argumentatie || '']) })
      }
      setStatus(null)
    } catch (err) {
      showStatus('error', 'Fout bij laden: ' + (err instanceof Error ? err.message : 'Onbekend'))
    } finally {
      setIsLoading(false)
    }
  }

  const downloadPDF = () => {
    if (!preview) return
    const doc = new jsPDF()
    doc.setFillColor(146, 0, 0)
    doc.rect(0, 0, 210, 30, 'F')
    doc.setTextColor(255, 255, 255)
    doc.setFontSize(20)
    doc.setFont('helvetica', 'bold')
    doc.text('KVH', 20, 20)
    doc.setFontSize(14)
    doc.setFont('helvetica', 'normal')
    doc.text(preview.title, 20, 25)
    doc.setFontSize(10)
    doc.text('Gegenereerd: ' + new Date().toLocaleString('nl-NL'), 130, 20)
    doc.setTextColor(0, 0, 0)

    if (preview.rows.length === 0) {
      doc.setFontSize(12)
      doc.text('Geen gegevens gevonden.', 20, 60)
    } else {
      autoTable(doc, {
        startY: 40,
        head: [preview.headers],
        body: preview.rows,
        theme: 'grid',
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [146, 0, 0], textColor: [255, 255, 255] },
        alternateRowStyles: { fillColor: [245, 245, 245] },
      })
    }

    const pageCount = (doc.internal as any).getNumberOfPages()
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i)
      doc.setFontSize(8)
      doc.setTextColor(128, 128, 128)
      doc.text(`Pagina ${i} van ${pageCount}`, 190, 285)
      doc.text('KVH Dashboard – Vertrouwelijk', 20, 285)
    }

    const fileName = preview.title.replace(/[^a-z0-9]/gi, '_').toLowerCase() + '_' + new Date().toISOString().split('T')[0] + '.pdf'
    doc.save(fileName)
    showStatus('success', 'PDF gedownload!')
  }

  const reportButtons = [
    { type: 'today',         label: 'Vandaag',          group: 'inspections' },
    { type: 'week',          label: 'Deze Week',         group: 'inspections' },
    { type: 'month',         label: 'Deze Maand',        group: 'inspections' },
    { type: 'problems-open', label: 'Open Meldingen',    group: 'problems' },
    { type: 'problems-all',  label: 'Alle Meldingen',    group: 'problems' },
  ]

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>Rapporten & Export</h2>

      {status && (
        <div className={`p-3 rounded-lg border flex items-center gap-2 text-sm ${
          status.type === 'success' ? 'bg-green-50 border-green-200 text-green-800' :
          status.type === 'error'   ? 'bg-red-50 border-red-200 text-red-800' :
                                      'bg-blue-50 border-blue-200 text-blue-800'
        }`}>
          {status.type === 'success' && <CheckCircle size={18} />}
          {status.type === 'error'   && <AlertCircle size={18} />}
          {status.type === 'info'    && <FileText size={18} />}
          {status.message}
        </div>
      )}

      <div className="card p-4 space-y-2" style={{ background: 'var(--bg)' }}>
        <h3 className="font-semibold text-sm" style={{ color: 'var(--text-secondary)' }}>Inspecties</h3>
        <div className="grid grid-cols-3 gap-2">
          {reportButtons.filter(b => b.group === 'inspections').map(b => (
            <button key={b.type} onClick={() => handlePreview(b.type)} disabled={isLoading}
              className="flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-sm font-medium transition-colors bg-green-600 hover:bg-green-700 text-white disabled:opacity-50">
              <Eye size={15} /> {b.label}
            </button>
          ))}
        </div>
        <h3 className="font-semibold text-sm pt-2" style={{ color: 'var(--text-secondary)' }}>Probleemmeldingen</h3>
        <div className="grid grid-cols-2 gap-2">
          {reportButtons.filter(b => b.group === 'problems').map(b => (
            <button key={b.type} onClick={() => handlePreview(b.type)} disabled={isLoading}
              className="flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-sm font-medium transition-colors bg-red-700 hover:bg-red-800 text-white disabled:opacity-50">
              <Eye size={15} /> {b.label}
            </button>
          ))}
        </div>
      </div>

      <div className="card p-4" style={{ background: 'var(--bg)' }}>
        <h3 className="font-semibold text-sm mb-2" style={{ color: 'var(--text-secondary)' }}>Email Rapporten</h3>
        <p className="text-sm mb-3" style={{ color: 'var(--text-secondary)' }}>Configureer automatische email rapporten in de instellingen</p>
        <button onClick={() => navigate('/settings')}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors">
          <Settings size={15} /> Ga naar Instellingen
        </button>
      </div>

      {/* Preview panel */}
      {preview && (
        <div className="card overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: 'var(--border)', background: 'var(--bg)' }}>
            <div>
              <h3 className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>{preview.title}</h3>
              <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{preview.rows.length} rijen</p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={downloadPDF}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-white transition-colors"
                style={{ background: 'var(--kvh-red)' }}>
                <Download size={14} /> Download PDF
              </button>
              <button onClick={() => setPreview(null)}
                className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                <X size={16} style={{ color: 'var(--text-secondary)' }} />
              </button>
            </div>
          </div>
          <div className="overflow-x-auto">
            {preview.rows.length === 0 ? (
              <p className="text-center py-8 text-sm" style={{ color: 'var(--text-secondary)' }}>Geen gegevens gevonden</p>
            ) : (
              <table className="w-full text-xs">
                <thead>
                  <tr style={{ background: 'var(--kvh-red)' }}>
                    {preview.headers.map(h => (
                      <th key={h} className="px-3 py-2 text-left text-white font-medium whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((row, i) => (
                    <tr key={i} className={i % 2 === 0 ? '' : 'bg-gray-50 dark:bg-gray-800/50'}>
                      {row.map((cell, j) => (
                        <td key={j} className="px-3 py-2 whitespace-nowrap max-w-[200px] truncate" style={{ color: 'var(--text-primary)' }} title={cell}>{cell}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
